/**
 * canonicalAdFilter — Frontend mirror of the backend's
 * `_build_public_live_ads_query()` (see `/app/backend/server.py`).
 *
 * iter320 — One source of truth for "is this ad visible to the
 * public Shop / Home grid?". Used by:
 *   • Home.js (defensive filter on `/api/ads` payload)
 *   • Shop.js (same defensive filter)
 *   • AdminOverview.jsx (the dev-only mismatch detector that
 *     compares Home / Shop visible counts against
 *     `ads.public_live` returned by the admin dashboard endpoint).
 *
 * The backend already filters /api/ads with this exact predicate so
 * Home and Shop will normally never see a "dead" ad. This helper
 * exists for:
 *   1. Defense-in-depth: if anything else (search, a hub feed,
 *      saved-ads) returns an ad that has since gone hidden /
 *      expired / etc, we still drop it before rendering.
 *   2. Dev-only mismatch detection: AdminOverview applies this
 *      predicate to whatever Home/Shop actually fetched and warns
 *      if Admin's `ads.public_live` count diverges.
 *
 * KEEP IN SYNC with `_build_public_live_ads_query()` in server.py.
 */

// Hidden categories — mirrors `PUBLIC_HIDDEN_CATEGORIES` in server.py.
// Legacy jobs ads are not surfaced on the public feed today.
export const PUBLIC_HIDDEN_CATEGORIES = ['jobs'];

// iter323 — Production safety: matches the backend
// PUBLIC_AD_TEST_TITLE_REGEX in server.py. Any ad whose title starts
// with one of these QA-fixture prefixes is dropped from public
// surfaces, defense-in-depth in case a backend cache returns a stale
// row. Real user titles never start with these tokens.
export const PUBLIC_AD_TEST_TITLE_REGEX = /^(CALIB_|TEST_|test_|Test_|E2E_|e2e_|QA_|qa_|DEMO_|demo_|MOCK_|mock_|SEED_|seed_|PLACEHOLDER)/;

// iter329 — Production feed integrity (P0). Mirrors the backend
// PUBLIC_AD_FAKE_USER_ID_REGEX. Blocks ads owned by historical seed/
// test/QA-harness user_ids even when the user doc itself is missing
// from db.users (legacy seed_mock_ads.py inserted orphan ads).
export const PUBLIC_AD_FAKE_USER_ID_REGEX = /^(seed-user-|seed_user_|seed-|seeduser|sample_|sample-|test-user-|test_user_|test_iter|qa[-_]|e2e[-_]|pwr_|notif_|push_|multi_login_|onboard_|authtest_|testuser_|cv_test_|profile_test_|subtitle_test_)/i;

// iter329 — Placeholder image hosts the legacy seeders used. Real
// seller ads come from Cloudinary, our /api/uploads/* path, or
// seller-uploaded URLs from facebook/whatsapp — never from picsum /
// loremflickr / placeholder CDNs.
export const PUBLIC_AD_PLACEHOLDER_IMAGE_HOSTS = [
  'picsum.photos',
  'loremflickr.com',
  'placeimg.com',
  'placehold.it',
  'placehold.co',
  'via.placeholder.com',
  'dummyimage.com',
  // iter329 — Legacy QA-fixture domains (test_hybrid_e2e_acceptance.sh)
  // that 404 → frontend shows generic SVG fallback → looks "mocked"
  // on Home/Shop. Blocking ensures the public feed never serves them.
  'example.com',
  'example.org',
  'example.net',
];

// iter332 — P0 production feed integrity round 2. Operator reported
// "Test Admin" seller appearing publicly on wassla.tech. Mirrors the
// backend PUBLIC_AD_FAKE_SELLER_NAME_REGEX so a denormalized seller
// name never reaches a real buyer's screen even when the owning
// user doc was missed at the API layer.
export const PUBLIC_AD_FAKE_SELLER_NAME_REGEX = new RegExp(
  '^\\s*(' +
    'test\\s+admin|' +
    'test\\s+user|' +
    'test\\s+seller|' +
    'testing\\s+agent|' +
    'seed\\s+user|' +
    'demo\\s+user|' +
    'mock\\s+user|' +
    'qa\\s+tester|' +
    'system\\s+user|' +
    'testing_agent|' +
    'sample\\s+seller' +
  ')',
  'i',
);

// iter333 — Synthetic `source` field block. The backend now rejects
// any ad whose `source` field reads as obviously test/seed/demo/mock/
// calibration/qa/e2e/fixture/testing_agent/system. We mirror the same
// list so a denormalized field never sneaks through.
export const PUBLIC_AD_FAKE_SOURCE_VALUES = new Set([
  'test',
  'seed',
  'demo',
  'mock',
  'calibration',
  'qa',
  'e2e',
  'fixture',
  'testing_agent',
  'seed_mock_ads',
  'seed_general_ads',
  'seed_sample_ads',
  'system',
  'system_user',
]);

const PLACEHOLDER_IMAGE_REGEX = new RegExp(
  `^https?://(?:[^/]*\\.)?(${PUBLIC_AD_PLACEHOLDER_IMAGE_HOSTS.map(h => h.replace(/\./g, '\\.')).join('|')})(/|$)`,
  'i',
);

function _allImagesArePlaceholders(images) {
  if (!Array.isArray(images) || images.length === 0) return false;
  return images.every((img) => {
    if (!img || typeof img !== 'string') return false;
    return PLACEHOLDER_IMAGE_REGEX.test(img);
  });
}

/**
 * @param {Object} ad
 * @returns {boolean} true if the ad would render on the public Shop/Home grid
 */
export function isPubliclyVisible(ad) {
  if (!ad || typeof ad !== 'object') return false;

  // 1. Status must be exactly "active". The backend canonical query
  //    is strict here — "published" / "live" / etc are NOT public.
  if (ad.status !== 'active') return false;

  // 2. `is_hidden === true` → owner hid the ad from public surfaces.
  if (ad.is_hidden === true) return false;

  // 3. Category is in the public-hidden list (currently just `jobs`).
  if (PUBLIC_HIDDEN_CATEGORIES.includes((ad.category || '').toLowerCase())) return false;

  // 4. Availability — backend accepts `available` or missing.
  //    "sold" / "reserved" / "suspected_unavailable" are NOT public.
  if (ad.availability_status && ad.availability_status !== 'available') return false;

  // 5. Expiry guard — backend filters `expires_at >= now`. We
  //    re-check client-side because the user's clock could be ahead
  //    of the backend by a few seconds and we don't want to render
  //    an ad that has already lapsed.
  if (ad.expires_at) {
    const exp = new Date(ad.expires_at).getTime();
    if (!Number.isNaN(exp) && exp < Date.now()) return false;
  }

  // 6. iter323 — Reject QA/test/seed/calibration ads regardless of
  //    where they came from. Matches the backend's triple lock so a
  //    CALIB_S1_clean row from a stale CDN response NEVER reaches a
  //    production buyer's screen.
  if (ad.is_test === true) return false;
  if (ad.is_seed === true) return false;
  if (ad.is_calibration === true) return false;
  if (ad.is_mock === true) return false;
  if (ad.is_demo === true) return false;
  // iter329 — Legacy camelCase flags used by seed_general_ads.py and
  // seed_sample_ads.py.
  if (ad.isSample === true) return false;
  if (ad.isGeneralSample === true) return false;
  if (ad.isMock === true) return false;
  if (ad.isDemo === true) return false;
  if (typeof ad.title === 'string' && PUBLIC_AD_TEST_TITLE_REGEX.test(ad.title)) return false;
  // iter329 — Block orphan seed ads (matching user_id pattern with
  // no corresponding db.users entry).
  if (typeof ad.user_id === 'string' && PUBLIC_AD_FAKE_USER_ID_REGEX.test(ad.user_id)) return false;
  // iter329 — Block ads whose ONLY images are placeholder CDNs.
  if (_allImagesArePlaceholders(ad.images)) return false;

  // iter332 — Block ads with obviously test/admin seller names so
  // a denormalized "Test Admin" / "Test User" never reaches a real
  // buyer. Mirrors PUBLIC_AD_FAKE_SELLER_NAME_REGEX on the backend.
  const sellerNames = [
    ad.seller_name,
    ad.user_name,
    ad.seller && ad.seller.name,
  ].filter((s) => typeof s === 'string' && s.length > 0);
  for (const name of sellerNames) {
    if (PUBLIC_AD_FAKE_SELLER_NAME_REGEX.test(name)) return false;
  }

  // iter333 — Block ads whose `source` field reads as synthetic.
  if (typeof ad.source === 'string' && PUBLIC_AD_FAKE_SOURCE_VALUES.has(ad.source.toLowerCase())) {
    return false;
  }

  return true;
}

/**
 * Filter + dedupe a list of ads to the canonical public-visible set.
 *
 * @param {Array<Object>} ads
 * @returns {Array<Object>} de-duplicated, predicate-passing ads
 */
export function getVisibleLiveMarketplaceAds(ads) {
  if (!Array.isArray(ads)) return [];
  const seen = new Set();
  const out = [];
  for (const ad of ads) {
    if (!isPubliclyVisible(ad)) continue;
    const id = ad.ad_id || ad.id || ad._id;
    if (!id) continue;          // ads without a stable id can't be counted
    if (seen.has(id)) continue;  // dedupe across merged collections
    seen.add(id);
    out.push(ad);
  }
  return out;
}

/**
 * Symmetric-difference of two ad_id lists. Used by the dev-only
 * mismatch detector in AdminOverview to log exactly which ad_ids
 * one surface saw that the other did not.
 *
 * @param {string[]} aIds
 * @param {string[]} bIds
 * @returns {{ inAOnly: string[], inBOnly: string[] }}
 */
export function diffAdIdSets(aIds, bIds) {
  const a = new Set(aIds || []);
  const b = new Set(bIds || []);
  const inAOnly = [];
  const inBOnly = [];
  a.forEach((id) => { if (!b.has(id)) inAOnly.push(id); });
  b.forEach((id) => { if (!a.has(id)) inBOnly.push(id); });
  return { inAOnly, inBOnly };
}
